
import { Component, Prop, Vue } from "vue-property-decorator";
import {
  ChartContent,
  ChartContentConfig,
} from "@fds/wm-id-chart-iq-content/src/content";
import {
  ChartIQContentDataProvider,
  ChartIQContentDataProviderConfig,
} from "@fds/wm-typescript-data-providers";
import {
  DataSegmentItem,
  ChartVariation,
  ChartWindow,
  InternalBenchmark,
} from "@/types";
import { logger } from "@/helpers/logger";
import { formatters } from "@/helpers/formatters";
import { nextTick } from "vue";
import BenchmarkLabels from "./chart-components/BenchmarkLabels.vue";
import DateRangePicker from "@/components/chart-components/DateRangePicker.vue";
import store from "@/store";

@Component({
  components: { BenchmarkLabels },
})
export default class ChartIQ extends Vue {
  //define that $refs of iframes will be handled as HTMLElements
  $refs!: {
    iframe: HTMLElement;
  };
  @Prop({ type: Number })
  private readonly idNotation!: number;
  private chartEngine!: ChartContent;
  private dataProvider!: ChartIQContentDataProvider;
  private isLoading = true;
  private hasError = false;
  private chartVariation!: ChartVariation;
  protected get chartSource(): string {
    return `${(this as any).chartSourcePrefix}/chartiq/fdsg/fdsg-chartiq.html`;
  }
  private reRender = 0;
  private benchmarks: Array<{
    id: number;
    color: string;
    text: string;
  }> = [];

  public async onLoadIframe(): Promise<void> {
    /* reverts customization to default when chart loads */
    window.localStorage.removeItem(`myChartLayout${this.idNotation}`);
    this.chartVariation =
      ChartVariation.Chartist; /* Basic, Advanced, Chartist */
    const chartIQContentDataProviderConfig =
      this.createChartIQContentDataProviderConfig();

    this.dataProvider = new ChartIQContentDataProvider(
      (this as any).mdg2Client,
      chartIQContentDataProviderConfig
    );

    const { iframe } = this.$refs;
    const chartContentConfig = this.createChartContentConfig();
    this.chartEngine = new ChartContent(iframe, chartContentConfig);
    this.chartEngine.attachDataHandler(this.dataProvider);
    // Mute this function as it damage the drop-downs
    // this.chartEngine.readySub.next = () => {};
    try {
      await this.chartEngine.init({
        idNotation: this.idNotation,
        uniqueId: this.idNotation,
        theme: "light",
        language: "en",
      });
      if (iframe) {
        const stxx = (window as unknown as ChartWindow).frames.chartIFrame.stxx;
        stxx.bypassRightClick = {
          series: true,
          study: false,
          drawing: false,
        };
        stxx.setGapLines({
          color: "black",
          width: 2,
        });
        iframe.style.visibility = "visible";
      }
      this.fixTranslations();
      this.injectCss();
      this.isLoading = false;
      this.injectDateRangePickerComponent();
      window.dispatchEvent(new Event("chart-loaded"));
    } catch (error) {
      logger.error(error);
      this.hasError = true;
      this.isLoading = false;
    }
  }
  public createChartIQContentDataProviderConfig(): ChartIQContentDataProviderConfig {
    const chartIQContentDataProviderConfig =
      new ChartIQContentDataProviderConfig();

    chartIQContentDataProviderConfig.API_PRICES_QUALITY = "BST";

    chartIQContentDataProviderConfig.FUNC_FORMAT_VALUE = function format(
      value: number | null,
      options: { idNotation: string; field: string }
    ) {
      if (options.field === "Volume") {
        return Number(value).toLocaleString("en-GB", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        });
      }
      return Number(value).toLocaleString("en-GB", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 4,
      });
    };

    chartIQContentDataProviderConfig.FUNC_FORMAT_DATE_TIME = function (
      value: number | Date,
      intraday: boolean
    ) {
      const dateValue = new Date(value);
      if (intraday) {
        return formatters.dateFormatter.format(dateValue, {
          locale: "en-GB",
          format: "onlyTime",
        });
      } else {
        return formatters.dateFormatter.format(dateValue, {
          locale: "en-GB",
          format: "onlyDate",
        });
      }
    };

    return chartIQContentDataProviderConfig;
  }
  public createChartContentConfig(): ChartContentConfig {
    const chartContentConfig = new ChartContentConfig();
    chartContentConfig.userProfile = this.chartVariation;
    chartContentConfig.chartType = "line";
    chartContentConfig.spanMultiplier = 1;
    chartContentConfig.spanBase = "year";
    chartContentConfig.showRanges = [
      {
        label: "1 d",
        multiplier: 1,
        base: "today",
        interval: "1",
        period: "1",
      },
      {
        label: "5 d",
        multiplier: 5,
        base: "day",
        interval: "1",
        period: "5",
      },
      {
        label: "1 m",
        multiplier: 1,
        base: "month",
        interval: "day",
        period: "1",
        hideSm: true,
      },
      {
        label: "3 m",
        multiplier: 3,
        base: "month",
        interval: "day",
        period: "1",
        hideSm: true,
      },
      {
        label: "6 m",
        multiplier: 6,
        base: "month",
        interval: "day",
        period: "1",
        hideSm: true,
      },
      {
        label: "ytd",
        multiplier: 1,
        base: "ytd",
        interval: "day",
        period: "1",
      },
      {
        label: "1 y",
        multiplier: 1,
        base: "year",
        interval: "day",
        period: "1",
      },
      {
        label: "3 y",
        multiplier: 3,
        base: "year",
        hideSm: true,
      },
      {
        label: "5 y",
        multiplier: 5,
        base: "year",
        hideSm: true,
      },
      {
        label: "10 y",
        multiplier: 10,
        base: "year",
        hideSm: true,
      },
      {
        label: "all",
        multiplier: 1,
        base: "all",
        hideSm: true,
      },
    ];
    return chartContentConfig;
  }

  public addBenchmark(idNotation: number) {
    if (!this.chartEngine) {
      return;
    }
    this.chartEngine.addBenchmark(idNotation);
    this.pollForBenchmark(idNotation)
      .then((internalBenchmark: InternalBenchmark) => {
        const exists = this.benchmarks.some((b) => b.id === idNotation);
        if (!exists) {
          this.benchmarks.push({
            id: idNotation,
            color: internalBenchmark.parameters.color,
            text: internalBenchmark.parameters.display,
          });
        }
      })
      .catch((err) => {
        console.error(err);
      });
  }
  public async getBenchmarks() {
    if ((window as unknown as ChartWindow).frames.chartIFrame.stxx.chart) {
      await nextTick();
      const activeBenchmarksData = (window as unknown as ChartWindow).frames
        .chartIFrame.stxx.chart?.series;
      return Object.keys(activeBenchmarksData as object).map((key) =>
        Number(key.split("#")[0])
      );
    }
    return [];
  }
  public async deleteBenchmark(notation: number) {
    if ((window as unknown as ChartWindow).frames.chartIFrame.stxx.chart) {
      await nextTick();
      const iframe = document.getElementsByName(
        "chartIFrame"
      )[0] as HTMLIFrameElement;
      const iframeDocument = iframe.contentDocument as Document;
      const comparisonItems = iframeDocument.querySelectorAll(
        "cq-comparison-item[cq-symbol]"
      );
      comparisonItems.forEach((item) => {
        if (item.getAttribute("cq-symbol")?.startsWith(notation.toString())) {
          const closeButton = item.querySelector(".ciq-close");
          if (closeButton) {
            const event = new MouseEvent("stxtap", {
              view: window,
              bubbles: true,
              cancelable: true,
            });
            closeButton.dispatchEvent(event);
          }
        }
      });
      this.benchmarks = this.benchmarks.filter((b) => b.id !== notation);
    }
  }
  private pollForBenchmark(idNotation: number): Promise<InternalBenchmark> {
    const pollingInterval = 100;
    const maxAttempts = 100; // 100 * 100ms = 10000ms (10 seconds)
    let attempts = 0;
    return new Promise((resolve, reject) => {
      const interval = setInterval(() => {
        attempts++;
        const activeBenchmarksData = (window as unknown as ChartWindow).frames
          .chartIFrame.stxx.chart?.series;
        if (activeBenchmarksData) {
          const key = Object.keys(activeBenchmarksData).find((k) =>
            k.startsWith(idNotation.toString())
          );
          if (key) {
            const internalBenchmark = (
              activeBenchmarksData as unknown as {
                [key: string]: InternalBenchmark;
              }
            )[key];
            clearInterval(interval);
            resolve(internalBenchmark);
            return;
          }
        }
        if (attempts >= maxAttempts) {
          clearInterval(interval);
          reject(
            new Error(
              "pollForBenchmark() error. Benchmark did not appear in time"
            )
          );
        }
      }, pollingInterval);
    });
  }
  public changeTheme(theme: string) {
    if (this.chartEngine) {
      const supportedThemes = ["light", "dark"];
      if (supportedThemes.includes(theme)) {
        this.chartEngine.changeTheme(theme);
      } else {
        throw Error(
          "changeTheme() error. Unsupported theme. Acceptable parameters are: 'light', 'dark'"
        );
      }
    }
  }
  private setHeadsUpDisplay(value: string) {
    (window as unknown as ChartWindow).frames.chartIFrame.stxx.layout.headsUp =
      value;
  }
  private getHeadsUpDisplay() {
    return (
      (window as unknown as ChartWindow).frames.chartIFrame.stxx.layout
        .headsUp ?? "null"
    );
  }
  private fixTranslations(): void {
    const rangeSelectorElement = (
      (window as unknown as ChartWindow).frames.chartIFrame as unknown as Window
    ).document.querySelector(".ciq-range-selector translate");
    const invertYAxisElement = (
      (window as unknown as ChartWindow).frames.chartIFrame as unknown as Window
    ).document.querySelector(".ciq-scale-invertyaxis translate");
    if (rangeSelectorElement) {
      rangeSelectorElement.setAttribute("original", "Range selector");
    }
    if (invertYAxisElement) {
      invertYAxisElement.setAttribute("original", "Invert Y-Axis");
    }
  }
  public changeLanguage(language: string) {
    if (this.chartEngine) {
      const currentHUD = this.getHeadsUpDisplay();
      this.setHeadsUpDisplay("dynamic");
      setTimeout(() => {
        this.setHeadsUpDisplay(currentHUD);
        this.chartEngine.changeLanguage(language);
        store.commit("setLanguage", language);
      }, 250);
    }
  }
  public setRange(fromDate: Date, toDate: Date) {
    if (this.chartEngine) {
      const today = new Date();
      if (fromDate < toDate && toDate < today) {
        this.chartEngine.setRange(fromDate, toDate);
      } else {
        throw Error(
          "setRange() error. Invalid dates selected. " +
            "Parameters must be instances of Date() and following must be true: fromDate < toDate && toDate < today"
        );
      }
    }
  }
  public getNotation() {
    return this.chartEngine.idNotation;
  }
  public setNotation(notation: number) {
    if (this.chartEngine) {
      try {
        this.benchmarks.forEach((b) => this.deleteBenchmark(b.id));
      } finally {
        this.benchmarks = [];
        this.chartEngine.idNotation = notation;
      }
    }
  }
  public reloadChart() {
    this.benchmarks = [];
    this.reRender += 1;
  }

  public injectDateRangePickerComponent() {
    const iframeWindow = (window as unknown as ChartWindow).frames.chartIFrame;
    const iframeDocument = (iframeWindow as unknown as Window).document;
    const footer = iframeDocument.querySelector(".ciq-footer");
    if (!footer) return;

    const container = iframeDocument.createElement("div");
    container.className = "custom-date-range-container";

    const rangesEl = footer.querySelector("cq-show-range");
    if (rangesEl) {
      footer.insertBefore(container, rangesEl);
    } else {
      footer.appendChild(container);
    }

    const poller = window.setInterval(() => {
      const segment = this.getDataSegment();
      if (segment && segment.length > 0) {
        clearInterval(poller);
        const first = segment[0];
        const last = segment[segment.length - 1];
        const initialFromDate = new Date(first.DT).toISOString().substr(0, 10);
        const initialToDate = new Date(last.DT).toISOString().substr(0, 10);

        new Vue({
          render: (h) =>
            h(DateRangePicker, {
              props: {
                initialFromDate,
                initialToDate,
              },
              on: {
                "apply-range": (range: { from: Date; to: Date }) => {
                  try {
                    this.setRange(range.from, range.to);
                  } catch (error) {
                    console.error(error);
                  }
                },
              },
            }),
        }).$mount(container);
      }
    }, 250);
  }

  public injectCss() {
    const iframeWindow = (window as unknown as ChartWindow).frames.chartIFrame;
    const iframeDocument = (iframeWindow as unknown as Window).document;
    const styleEl = iframeDocument.createElement("style");
    styleEl.textContent = `
      @keyframes fadeIn {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }
      .ciq-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
      .custom-date-range-container {
        flex: 0 0 auto;
      }
      cq-show-range {
        flex: 0 1 auto;
        margin-left: auto;
      }
      .date-range-picker {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        animation: fadeIn 0.2s ease;
      }
      cq-comparison-item {
        display: none;
      }
    `;
    if (iframeDocument.head) {
      iframeDocument.head.appendChild(styleEl);
    }
  }

  public getDataSegment(): DataSegmentItem[] {
    const chart = (window as unknown as ChartWindow).frames.chartIFrame?.stxx
      ?.chart;
    return chart && chart.dataSegment ? chart.dataSegment : [];
  }
}
